import { useCallback, useEffect, useRef, useState } from "react";
import { useSelector } from "react-redux";
import { toast } from "react-toastify";

import { facilityMapRequestAtom } from "atoms/map/request/facilities";
import { wellMapRequestAtom } from "atoms/map/request/wells";
import { DEFAULT_MAX_BINS } from "constants/mapSeries.constants";
import { useAtomValue } from "jotai";
import { isEmptyValue, parseNumber } from "utils/fp";

import { EntityKind } from "models/entityKind";

import { RootState } from "../../store/rootReducer";

const TOAST_AUTO_CLOSE_MS = 5000;

// Module-level set to persist across component re-renders and track notified fields
const globalNotifiedFields = new Set<string>();

export function resetNotifiedFields() {
  globalNotifiedFields.clear();
}

type MaxBinsValue = Maybe<string | number>;

function isEmptyMaxBins(value: unknown): boolean {
  return isEmptyValue(value) || value === 0 || value === "0";
}

function useCurrentMaxBins() {
  const wellRequest = useAtomValue(wellMapRequestAtom);
  const facilityRequest = useAtomValue(facilityMapRequestAtom);
  const activeEntities = useSelector((state: RootState) => state.app.activeEntityKinds);

  const isWellActive = activeEntities.includes(EntityKind.Well);

  return {
    groupByField: isWellActive
      ? wellRequest?.GroupBy?.groupByField
      : facilityRequest?.GroupBy?.groupByField,
    maxBins: isWellActive
      ? wellRequest?.GroupBy?.bin?.MaxBins
      : facilityRequest?.GroupBy?.bin?.MaxBins
  };
}

/**
 * Hook to track MaxBins value transitions per field
 * Returns whether a transition from empty to a number was detected
 */
function useMaxBinsTransition(groupByField: string | undefined, maxBins: MaxBinsValue) {
  const prevMaxBinsRef = useRef<Record<string, MaxBinsValue>>({});
  const seenFieldsRef = useRef<Set<string>>(new Set());
  const [transition, setTransition] = useState<{
    shouldNotify: boolean;
    maxBinNum: number;
  }>({
    shouldNotify: false,
    maxBinNum: 0
  });

  const resetShouldNotify = useCallback(() => {
    setTransition((prev) => ({ ...prev, shouldNotify: false }));
  }, []);

  useEffect(() => {
    if (!groupByField) {
      setTransition({ shouldNotify: false, maxBinNum: 0 });
      return;
    }

    const prevMaxBins = prevMaxBinsRef.current[groupByField];
    const isFirstTimeSeeing = !seenFieldsRef.current.has(groupByField);
    // Use module-level set for persistent tracking across re-renders
    const alreadyNotified = globalNotifiedFields.has(groupByField);
    const maxBinNum = parseNumber(maxBins, DEFAULT_MAX_BINS);

    // Update tracking refs
    seenFieldsRef.current.add(groupByField);
    prevMaxBinsRef.current[groupByField] = maxBins;

    // Determine if we should notify:
    // - Not first time seeing the field
    // - Current value is not empty
    // - Current value is >= DEFAULT_MAX_BINS (max series limit reached)
    // - Previous value was empty
    // - Haven't already notified for this field (checked against global set)
    const shouldNotify =
      !isFirstTimeSeeing &&
      !isEmptyMaxBins(maxBins) &&
      maxBinNum >= DEFAULT_MAX_BINS &&
      isEmptyMaxBins(prevMaxBins) &&
      !alreadyNotified;

    // Mark field as notified in global set if we're going to notify
    if (shouldNotify) {
      globalNotifiedFields.add(groupByField);
    }

    setTransition({ shouldNotify, maxBinNum });
  }, [groupByField, maxBins]);

  return { ...transition, resetShouldNotify };
}

function useMaxBinsToast() {
  const [toastShown, setToastShown] = useState(false);
  const isShowingToastRef = useRef(false);

  const showToast = useCallback(
    (maxBins: number) => {
      if (isShowingToastRef.current || toastShown) {
        return false;
      }

      isShowingToastRef.current = true;
      setToastShown(true);

      toast.info(
        `Showing only the first ${maxBins} series because the large series option was declined. Adjust bin settings, focus, or filters to load more.`,
        {
          autoClose: TOAST_AUTO_CLOSE_MS,
          hideProgressBar: false,
          closeOnClick: true,
          pauseOnHover: true,
          draggable: true,
          onClose: () => {
            isShowingToastRef.current = false;
            setToastShown(false);
          }
        }
      );

      return true;
    },
    [toastShown]
  );

  return { showToast, isShowing: isShowingToastRef.current || toastShown };
}

export function MaxBinsToastNotifier() {
  const { groupByField, maxBins } = useCurrentMaxBins();
  const { shouldNotify, maxBinNum, resetShouldNotify } = useMaxBinsTransition(
    groupByField,
    maxBins
  );
  const { showToast, isShowing } = useMaxBinsToast();

  useEffect(() => {
    if (shouldNotify && !isShowing) {
      const toastShown = showToast(maxBinNum);
      if (toastShown) {
        resetShouldNotify();
      }
    }
  }, [shouldNotify, maxBinNum, isShowing, showToast, resetShouldNotify]);

  return null;
}
